import React, { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import ArrowDownBlackIcon from "../../../assets/icons/ArrowDownBlackIconShowroom";
import { useBlockFlatStatistic } from "../../../queries/queries";
import { BASE_URL_VISUAL } from "../../../services/RequestService";
import { defaultImage } from "../../../utils/constants/defaultImage";
import styles from "../visual.module.scss";
import { useTranslation } from "react-i18next";

/** Min bounding-box side (px) at which labels use ~1x scale (legacy max fontSize was ~14). */
const POLY_TEXT_REF_PX = 100;
const MAX_POLY_TEXT_SCALE = 1;
const MIN_POLY_TEXT_SCALE = 0.35;

function clampPolyTextScale(polyMinSide: number) {
  const t = polyMinSide / POLY_TEXT_REF_PX;
  return Math.min(MAX_POLY_TEXT_SCALE, Math.max(MIN_POLY_TEXT_SCALE, t));
}

function flatRectWidth(e: string) {
  return e?.length > 1 ? (e?.length - 1 || 0) * 10 : 0;
}

interface IProps {
  setOpenDrawer: React.Dispatch<
    React.SetStateAction<{
      id: number;
      visible: boolean;
    }>
  >;
  flatId: number;
}

const Floor: FC<IProps> = ({ setOpenDrawer, flatId }) => {
  const { t } = useTranslation();
  const statusParam = new URLSearchParams(window.location.search).get(
    "notStatus"
  );
  const isStatus = statusParam === "true";

  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState({
    width: 566,
    height: 566,
    clientWidth: 0,
  });
  const params = useParams();

  const { data } = useBlockFlatStatistic(
    Number(params.block),
    Number(params.floor)
  );

  const loadImage = (e: any) => {
    setImageSize({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight,
      clientWidth: e.target.clientWidth,
    });
  };

  useEffect(() => {
    const resize = new ResizeObserver((entries) => {
      const [entry] = entries;
      setImageSize((prev) => ({
        // @ts-expect-error
        width: entry?.target?.naturalWidth,
        // @ts-expect-error
        height: entry?.target?.naturalHeight,
        clientWidth: entry?.target.clientWidth,
      }));
    });

    if (imageRef.current) {
      resize.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resize.unobserve(imageRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageRef.current]);

  const statusColor = (type: string) => {
    const color = {
      SOLD: "rgba(30, 203, 137, 0.9)",
      BOOKED: "rgba(249, 218, 141, 0.9)",
      RESERVE: "rgba(249, 218, 141, 0.9)",
      SALE: "rgba(91, 93, 101, 0.9)",
    }[type];

    return color || "rgba(30, 203, 137, 0.9)";
  };

  const statusColorUgam = (type: string) => {
    const color = {
      SOLD: "rgba(91, 93, 101, 0.9)",
      BOOKED: "rgba(91, 93, 101, 0.9)",
      RESERVE: "rgba(91, 93, 101, 0.9)",
      SALE: "rgba(30, 203, 137, 0.9)",
    }[type];

    return color || "rgba(91, 93, 101, 0.9)";
  };

  return (
    <div className={styles.visual}>
      <span
        className={styles.visual_back}
        onClick={() => window.history.back()}
      >
        <ArrowDownBlackIcon />{" "}
        <span style={{ color: "#000000" }}>{t("ortga")}</span>
      </span>
      <div className={styles.visual_floor}>
        <div
          className={styles.visual_floor_child}
          style={{ minWidth: imageSize?.clientWidth }}
        >
          <img
            ref={imageRef}
            src={
              data?.buildingFloorImagePath
                ? BASE_URL_VISUAL + data?.buildingFloorImagePath
                : defaultImage
            }
            alt=""
            onLoad={(e) => {
              loadImage(e.currentTarget);
            }}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
            viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
          >
            {data?.buildingFloorImagePath &&
              data?.blockFlatDataDtoList.map((item) => {
                const xArr: number[] = item?.flatImage
                  ? item.flatImage
                      .split(" ")
                      .map((pair: string) => pair.split(","))
                      .map((arr: string[]) => Number(arr[0]))
                      .filter((i: number) => !Number.isNaN(i) && i !== 0)
                  : [];
                const yArr: number[] = item?.flatImage
                  ? item.flatImage
                      .split(" ")
                      .map((pair: string) => pair.split(","))
                      .map((arr: string[]) => Number(arr[1]))
                      .filter((i: number) => !Number.isNaN(i) && i !== 0)
                  : [];

                const hasBounds = xArr.length > 0 && yArr.length > 0;
                const minX = hasBounds ? Math.min(...xArr) : 0;
                const maxX = hasBounds ? Math.max(...xArr) : 0;
                const minY = hasBounds ? Math.min(...yArr) : 0;
                const maxY = hasBounds ? Math.max(...yArr) : 0;
                const x = hasBounds ? maxX - (maxX - minX) / 2 : 0;
                const y = hasBounds ? maxY - (maxY - minY) / 2 : 0;
                const polyMinSide = hasBounds
                  ? Math.min(maxX - minX, maxY - minY) || POLY_TEXT_REF_PX
                  : POLY_TEXT_REF_PX;
                const s = hasBounds ? clampPolyTextScale(polyMinSide) : 1;
                const roomLabelW =
                  (25 + flatRectWidth(String(item.flatRooms ?? ""))) * s;

                return (
                  <Link
                    to={window.location.pathname + window.location.search}
                    key={item.flatId}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDrawer({
                        visible: true,
                        id: item.flatId,
                      });
                    }}
                    style={{
                      pointerEvents:
                        item.flatStatus === "SALE" ? "inherit" : "none",
                      zIndex: item.flatStatus === "SALE" ? "0" : "1",
                    }}
                  >
                    <polygon
                      className="st0"
                      points={item.flatImage}
                      fill={
                        isStatus
                          ? statusColorUgam(item.flatStatus)
                          : statusColor(item.flatStatus)
                      }
                      style={{
                        fill: item.flatId === flatId ? "#f9da8d50" : "",
                      }}
                    />
                    {hasBounds &&
                      (isStatus ? (
                        <g transform={`translate(${x},${y})`}>
                          <rect
                            x={-20 * s}
                            y={-20 * s}
                            width={40 * s}
                            height={40 * s}
                            rx={20 * s}
                            fill="#A4A4A4"
                          />
                          <text
                            x={0}
                            y={0}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={13 * s}
                          >
                            {item.flatRooms}X
                          </text>
                        </g>
                      ) : (
                        <>
                          {item.flatStatus === "SOLD" ? (
                            <g transform={`translate(${x},${y})`}>
                              <rect
                                x={-35 * s}
                                y={-10 * s}
                                width={70 * s}
                                height={20 * s}
                                rx={12.5 * s}
                                fill="#ffffff"
                              />
                              <text
                                x={0}
                                y={0}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{ fontSize: 14 * s }}
                              >
                                {t("Sotilgan")}
                              </text>
                            </g>
                          ) : item.flatStatus === "BOOKED" ||
                            item.flatStatus === "RESERVE" ? (
                            <g transform={`translate(${x},${y})`}>
                              <rect
                                x={-35 * s}
                                y={-10 * s}
                                width={70 * s}
                                height={20 * s}
                                rx={12.5 * s}
                                fill="#ffffff"
                              />
                              <text
                                x={0}
                                y={0}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{ fontSize: 14 * s }}
                              >
                                {t("Band qilingan")}
                              </text>
                            </g>
                          ) : (
                            <g transform={`translate(${x},${y})`}>
                              <rect
                                rx={10 * s}
                                x={-roomLabelW / 2}
                                y={-11 * s}
                                fill="#A4A4A4"
                                width={roomLabelW}
                                height={22 * s}
                              />
                              <text
                                x={0}
                                y={0}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={13 * s}
                              >
                                {item.flatRooms}X
                              </text>
                            </g>
                          )}
                        </>
                      ))}
                  </Link>
                );
              })}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default Floor;
